/**
 * Campaign door copy — EN + AR (MENA business tone).
 * House shape: every string is `{ en, ar }`, resolved through `useLocale()`.
 * Same family as the broadcast door (components/paid-ads/broadcast-door/copy.ts)
 * — shared chrome lives in ../door-shared.tsx, copy stays per-feature.
 */

export type CampaignDoorLocale = 'en' | 'ar'

export type LocalizedString = { en: string; ar: string }

export function at(entry: LocalizedString, locale: CampaignDoorLocale): string {
  return entry[locale] || entry.en
}

export function t(
  entry: LocalizedString,
  locale: CampaignDoorLocale,
  vars?: Record<string, string | number>,
): string {
  let value = at(entry, locale)
  if (vars) {
    for (const [k, v] of Object.entries(vars)) {
      value = value.split(`{${k}}`).join(String(v))
    }
  }
  return value
}

export const CAMPAIGN_DOOR_COPY = {
  'door.title': { en: 'New campaign', ar: 'حملة جديدة' },
  'door.description': {
    en: 'Start a paid campaign as a draft. Choose how to build it — everything stays a draft until launch wiring ships after strategy F6.',
    ar: 'ابدأ حملة مدفوعة كمسودة. اختر طريقة إنشائها — تبقى كل شيء مسودة حتى يكتمل التفعيل بعد استراتيجية F6.',
  },
  'door.objective.label': { en: 'Objective', ar: 'الهدف' },
  'door.objective.placeholder': {
    en: 'e.g. Sell the Mar Mikhael loft this month',
    ar: 'مثال: بيع شقة مار مخايل هذا الشهر',
  },

  'avenue.predefined.label': { en: 'From a template', ar: 'من قالب جاهز' },
  'avenue.predefined.hint': {
    en: 'Platform-prepared campaign for your market — targeting and recommended spend included.',
    ar: 'حملة معدّة من المنصة لسوقك — تشمل الاستهداف والميزانية الموصى بها.',
  },
  'avenue.ai.label': { en: 'Let AI propose', ar: 'دع الذكاء الاصطناعي يقترح' },
  'avenue.ai.hint': {
    en: 'The AI proposes objective, spend and targeting for your market — you review and confirm.',
    ar: 'يقترح الذكاء الاصطناعي الهدف والميزانية والاستهداف لسوقك — وأنت تراجع وتؤكد.',
  },
  'avenue.manual.label': { en: 'Start from scratch', ar: 'البدء من الصفر' },
  'avenue.manual.hint': {
    en: 'Name it, set the objective and spend, pick the territory.',
    ar: 'سمِّ الحملة، حدد الهدف والميزانية، واختر النطاق الجغرافي.',
  },

  'template.empty': {
    en: 'No active templates for your market yet — pick another avenue.',
    ar: 'لا توجد قوالب نشطة لسوقك بعد — اختر طريقة أخرى.',
  },
  'template.recommended': { en: 'recommended spend', ar: 'الميزانية الموصى بها' },

  'paid.budget.label': { en: 'Spend', ar: 'الميزانية' },
  'paid.budget.capsHelper': {
    en: 'For {country}, spend must be between {min} and {max}.',
    ar: 'بالنسبة إلى {country}، يجب أن تكون الميزانية بين {min} و{max}.',
  },
  'paid.budget.capsMinOnly': {
    en: 'For {country}, spend must be at least {min}.',
    ar: 'بالنسبة إلى {country}، يجب ألا تقل الميزانية عن {min}.',
  },
  'paid.budget.capsMaxOnly': {
    en: 'For {country}, spend must be at most {max}.',
    ar: 'بالنسبة إلى {country}، يجب ألا تتجاوز الميزانية {max}.',
  },
  'paid.name.label': { en: 'Campaign name', ar: 'اسم الحملة' },
  'paid.name.placeholder': { en: 'e.g. Hamra 2-bed — June push', ar: 'مثال: الحمرا — شقق غرفتين، حملة حزيران' },
  'paid.targeting.country.label': { en: 'Country', ar: 'الدولة' },
  'paid.targeting.country.helper': {
    en: 'Locked to your market ({country}).',
    ar: 'مثبّت على سوقك ({country}).',
  },
  'paid.targeting.areas.label': { en: 'Areas (optional)', ar: 'المناطق (اختياري)' },
  'paid.targeting.areas.placeholder': {
    en: 'Comma-separated area ids, e.g. beirut_hamra',
    ar: 'معرّفات المناطق مفصولة بفواصل، مثال: beirut_hamra',
  },
  'paid.targeting.radius.label': { en: 'Radius around areas, meters (optional)', ar: 'نطاق حول المناطق، بالمتر (اختياري)' },

  'ai.helper': {
    en: 'The AI proposes based on your market. Review and edit — nothing is created until you confirm.',
    ar: 'يقترح الذكاء الاصطناعي بناءً على سوقك. راجع المقترح وعدّله — لن يُنشأ شيء قبل تأكيدك.',
  },
  'ai.propose': { en: 'Propose with AI', ar: 'اقترح بالذكاء الاصطناعي' },
  'ai.proposing': { en: 'Asking the AI…', ar: 'جارٍ سؤال الذكاء الاصطناعي…' },
  'ai.unavailable': {
    en: 'AI proposals are not available right now ({code}). Use a template or type the plan yourself.',
    ar: 'مقترحات الذكاء الاصطناعي غير متاحة حالياً ({code}). استخدم قالباً أو أدخل الخطة يدوياً.',
  },

  'door.denied.title': { en: 'Paid ads are not available', ar: 'الإعلانات المدفوعة غير متاحة' },
  'door.denied.upsell': {
    en: 'Upgrade your plan to include paid ads, or contact the platform administrator if you believe this is a mistake.',
    ar: 'رقِّ خطتك لتشمل الإعلانات المدفوعة، أو تواصل مع مسؤول المنصة إذا كنت تعتقد أن هذا خطأ.',
  },

  'door.created.title': { en: 'Campaign draft created', ar: 'أُنشئت مسودة الحملة' },
  'door.created.body': {
    en: 'The campaign stays a draft. Launch wiring ships after strategy F6.',
    ar: 'تبقى الحملة مسودة. التفعيل يأتي بعد استراتيجية F6.',
  },

  'door.submit.predefined': { en: 'Create from template', ar: 'إنشاء من القالب' },
  'door.submit.ai': { en: 'Confirm AI campaign', ar: 'تأكيد حملة الذكاء الاصطناعي' },
  'door.submit.manual': { en: 'Create campaign draft', ar: 'إنشاء مسودة الحملة' },
  'door.submit.creating': { en: 'Creating…', ar: 'جارٍ الإنشاء…' },
  'door.cancel': { en: 'Cancel', ar: 'إلغاء' },
  'door.error.generic': { en: 'Something went wrong', ar: 'حدث خطأ ما' },
} as const

export type CampaignDoorCopyKey = keyof typeof CAMPAIGN_DOOR_COPY

export function cdc(
  key: CampaignDoorCopyKey,
  locale: CampaignDoorLocale,
  vars?: Record<string, string | number>,
): string {
  return t(CAMPAIGN_DOOR_COPY[key], locale, vars)
}

/** Micros → major units for helper text (same convention as the broadcast door). */
export function formatMicros(micros: number): string {
  return Number.isInteger(micros / 1_000_000) ? String(micros / 1_000_000) : (micros / 1_000_000).toFixed(2)
}
